import { useEffect, useState } from "react"

export type UseTypingPlaceholderOptions = {
  text: string
  typingDurationMs?: number
  holdDurationMs?: number
  cursorBlinkMs?: number
  cursorChar?: string
}

const DEFAULT_TYPING_DURATION_MS = 4500
const DEFAULT_HOLD_DURATION_MS = 2000
const DEFAULT_CURSOR_BLINK_MS = 270
const DEFAULT_CURSOR_CHAR = "|"

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

// Mimics the "typing-reveal" motion from the video-hero Figma design: types
// the text out over `typingDurationMs`, holds it for `holdDurationMs`, then
// restarts — with a blinking cursor throughout, ONESTORE-527.
export default function useTypingPlaceholder({
  text,
  typingDurationMs = DEFAULT_TYPING_DURATION_MS,
  holdDurationMs = DEFAULT_HOLD_DURATION_MS,
  cursorBlinkMs = DEFAULT_CURSOR_BLINK_MS,
  cursorChar = DEFAULT_CURSOR_CHAR,
}: UseTypingPlaceholderOptions): string {
  const [visibleChars, setVisibleChars] = useState(0)
  const [isCursorVisible, setIsCursorVisible] = useState(true)
  const [isReducedMotion] = useState(prefersReducedMotion)

  useEffect(() => {
    if (isReducedMotion) {
      setVisibleChars(text.length)
      setIsCursorVisible(false)
      return undefined
    }

    if (text.length === 0) {
      setVisibleChars(0)
      return undefined
    }

    const stepMs = typingDurationMs / text.length
    let charIndex = 0
    let timeoutId: ReturnType<typeof setTimeout>

    const typeNextChar = (): void => {
      charIndex += 1
      setVisibleChars(charIndex)

      if (charIndex < text.length) {
        timeoutId = setTimeout(typeNextChar, stepMs)
      } else {
        timeoutId = setTimeout(() => {
          charIndex = 0
          setVisibleChars(0)
          timeoutId = setTimeout(typeNextChar, stepMs)
        }, holdDurationMs)
      }
    }

    timeoutId = setTimeout(typeNextChar, stepMs)

    return () => clearTimeout(timeoutId)
  }, [text, typingDurationMs, holdDurationMs, isReducedMotion])

  useEffect(() => {
    if (isReducedMotion || text.length === 0) {
      return undefined
    }

    const blinkIntervalId = setInterval(() => {
      setIsCursorVisible((current) => !current)
    }, cursorBlinkMs)

    return () => clearInterval(blinkIntervalId)
  }, [cursorBlinkMs, isReducedMotion, text.length])

  return `${text.slice(0, visibleChars)}${isCursorVisible ? cursorChar : ""}`
}
